// AGC: Fields API Service
// AGC: Handles all API calls related to fields
// AGC: All fields are filtered by user account - each user only sees their own fields

import { getBackendUrl } from '../utils/backendUrl'
import type { Field } from '../types'

// AGC: Backend URL for API calls
const BASE_URL = getBackendUrl()

/**
 * AGC: Get all fields from backend for the authenticated user
 * AGC: Requires authentication token - fields are filtered by userId
 */
export const getFields = async (authToken?: string | null): Promise<Field[]> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    // AGC: Add authentication token if provided
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/fields`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      throw new Error(`Failed to fetch fields: ${response.statusText}`)
    }

    const data = await response.json()
    return data.fields || []
  } catch (error) {
    console.error('AGC: Error fetching fields:', error)
    // AGC: Return empty array on error (graceful degradation) - new accounts start empty
    return []
  }
}

/**
 * AGC: Create a new field for the authenticated user
 * AGC: Requires authentication token - field is associated with userId
 */
export const createField = async (
  fieldData: Partial<Field>,
  authToken?: string | null
): Promise<Field> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    // AGC: Add authentication token if provided
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/fields`, {
      method: 'POST',
      headers,
      body: JSON.stringify(fieldData),
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to create field: ${response.statusText}`)
    }

    const data = await response.json()
    return data.field
  } catch (error) {
    console.error('AGC: Error creating field:', error)
    throw error
  }
}

/**
 * AGC: Update an existing field
 * AGC: Requires authentication token
 */
export const updateField = async (
  fieldId: string,
  updates: Partial<Field>,
  authToken?: string | null
): Promise<Field> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/fields/${fieldId}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(updates),
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to update field: ${response.statusText}`)
    }

    const data = await response.json()
    return data.field
  } catch (error) {
    console.error('AGC: Error updating field:', error)
    throw error
  }
}

/**
 * AGC: Delete a field
 * AGC: Requires authentication token
 * AGC: Should only be called if field has no plots
 */
export const deleteField = async (
  fieldId: string,
  authToken?: string | null
): Promise<void> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }    const response = await fetch(`${BASE_URL}/api/v1/fields/${fieldId}`, {
      method: 'DELETE',
      headers,
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to delete field: ${response.statusText}`)
    }
  } catch (error) {
    console.error('AGC: Error deleting field:', error)
    throw error
  }
}

/**
 * AGC: Move a plot from one field to another
 * AGC: Used for merging farms
 */
export const movePlotToField = async (
  sourceFieldId: string,
  plotId: string,
  targetFieldId: string,
  authToken?: string | null
): Promise<void> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/fields/${sourceFieldId}/plots/${plotId}/move`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ targetFieldId }),
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to move plot: ${response.statusText}`)
    }
    
    console.log('✅ AGC: Plot moved successfully:', plotId, 'to', targetFieldId)
  } catch (error) {
    console.error('AGC: Error moving plot:', error)
    throw error
  }
}
